import * as React from 'react'
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'

import {
  formatCalendarDate,
  parseCalendarDate,
  toCalendarDateString,
} from '@/shared/lib/tenantDateTime'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { CalendarGrid, MONTHS_FULL, isBeforeDay, isAfterDay } from '@/shared/ui/calendar-grid'

// Strips all non-digit characters from a masked input string.
function extractDigits(text: string): string {
  return text.replace(/\D/g, '')
}

// Formats up to 8 digits as MM/DD/YYYY, inserting slashes at the correct positions.
function formatMask(digits: string): string {
  const limited = digits.slice(0, 8)
  if (limited.length <= 2) return limited
  if (limited.length <= 4) return `${limited.slice(0, 2)}/${limited.slice(2)}`
  return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`
}

// Parses exactly 8 digits (MMDDYYYY) into a Date; returns null for invalid dates (e.g. Feb 30).
function parseMaskDigits(digits: string): Date | null {
  if (digits.length !== 8) return null
  const month = parseInt(digits.slice(0, 2), 10)
  const day = parseInt(digits.slice(2, 4), 10)
  const year = parseInt(digits.slice(4, 8), 10)
  const date = new Date(year, month - 1, day)
  // Reject rollover dates — JS silently normalises Feb 30 → Mar 2, etc.
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

interface DatePickerInputProps {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minDate?: Date
  maxDate?: Date
  // When true, replaces the Button trigger with a masked text input + calendar icon button.
  // Apply only to filter surfaces (bsDatepicker parity) — NOT to form fields (REBUILD-430).
  allowTyping?: boolean
}

export function DatePickerInput({
  id,
  value,
  onChange,
  placeholder = '—',
  disabled,
  className,
  minDate,
  maxDate,
  allowTyping = false,
}: DatePickerInputProps) {
  const [open, setOpen] = React.useState(false)
  const selected = parseCalendarDate(value) ?? undefined
  const today = React.useMemo(() => new Date(), [])

  const [viewYear, setViewYear] = React.useState(
    () => selected?.getFullYear() ?? today.getFullYear(),
  )
  const [viewMonth, setViewMonth] = React.useState(() => selected?.getMonth() ?? today.getMonth())

  // Text state for the allowTyping branch; always initialised but only rendered when allowTyping.
  const [inputText, setInputText] = React.useState(() => (value ? formatCalendarDate(value) : ''))

  // This effect intentionally omits `value`, `allowTyping`, `selected`, and `today` from its
  // dependency array — it only reacts to the `open` transition. When `open` changes, React
  // runs the effect with the closures from the LATEST render, so the values are always current
  // at execution time. Adding them as deps would re-run on every keystroke (via `value`),
  // incorrectly resetting `viewYear`/`viewMonth` while the popover is open.
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (open) {
      const base = selected ?? today
      setViewYear(base.getFullYear())
      setViewMonth(base.getMonth())
    } else if (allowTyping) {
      // Reset any incomplete typed text when the popover closes without committing.
      setInputText(value ? formatCalendarDate(value) : '')
    }
  }, [open])
  /* eslint-enable react-hooks/exhaustive-deps */

  // Sync inputText whenever value changes externally (calendar pick propagates back as prop).
  // Guard with allowTyping to avoid a wasted re-render when the text input is not rendered.
  React.useEffect(() => {
    if (!allowTyping) return
    setInputText(value ? formatCalendarDate(value) : '')
  }, [value, allowTyping])

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear((y) => y - 1)
    } else setViewMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear((y) => y + 1)
    } else setViewMonth((m) => m + 1)
  }

  const handleSelect = (date: Date) => {
    onChange(toCalendarDateString(date))
    setOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setOpen(false)
  }

  // Popover trigger is a Button (not a text input), so Delete/Backspace would
  // otherwise do nothing. Clear the value when the focused trigger has a date
  // (REBUILD-397 / JW 400 — supply/lot optional dates). Ignore key-repeat and
  // modifier combos so OS/browser shortcuts do not fire repeated clears.
  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (event.key !== 'Backspace' && event.key !== 'Delete') return
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return
    if (!value) return

    event.preventDefault()
    event.stopPropagation()
    handleClear()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawDigits = extractDigits(event.target.value)
    const masked = formatMask(rawDigits)
    // Note: setting inputText to the re-masked value resets the cursor to the end of the input.
    // This is a known limitation of this mask approach — cursor position is not preserved when
    // editing in the middle of the field. Acceptable UX for a simple MM/DD/YYYY filter mask.
    setInputText(masked)

    if (rawDigits.length === 8) {
      const parsed = parseMaskDigits(rawDigits)
      if (parsed) {
        // Reject dates outside the allowed range (consistent with CalendarGrid behaviour).
        if ((minDate && isBeforeDay(parsed, minDate)) || (maxDate && isAfterDay(parsed, maxDate)))
          return
        onChange(toCalendarDateString(parsed))
        if (open) setOpen(false)
      }
    }
  }

  const handleInputBlur = () => {
    const rawDigits = extractDigits(inputText)
    if (rawDigits.length === 0) {
      // Only fire onChange when there is an actual value to clear — avoids a redundant
      // router.get() navigation on filter pages when the user tabs through an already-empty field.
      if (value !== '') onChange('')
    } else if (rawDigits.length < 8) {
      // Incomplete: restore from last committed value.
      setInputText(value ? formatCalendarDate(value) : '')
    } else {
      // 8 digits: already committed if the date was valid. Revert display if:
      //   a) parseMaskDigits rejected it (e.g. Feb 30 — rolls over to Mar 2 in JS), or
      //   b) the date is valid but outside the allowed range (minDate/maxDate) — in that
      //      case handleInputChange returned early without calling onChange, so the display
      //      would diverge from the actual filter value without this guard.
      const parsed = parseMaskDigits(rawDigits)
      const isOutOfRange =
        parsed !== null &&
        ((minDate !== undefined && isBeforeDay(parsed, minDate)) ||
          (maxDate !== undefined && isAfterDay(parsed, maxDate)))
      if (!parsed || isOutOfRange) {
        setInputText(value ? formatCalendarDate(value) : '')
      }
    }
  }

  const formatted = value ? formatCalendarDate(value) : null

  const calendarPopoverContent = (
    <PopoverContent className="w-auto p-3" align="start">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={prevMonth}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium">
            {MONTHS_FULL[viewMonth]} {viewYear}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={nextMonth}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <CalendarGrid
          viewYear={viewYear}
          viewMonth={viewMonth}
          selected={selected}
          today={today}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={handleSelect}
        />

        <div className="flex justify-between border-t pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('text-muted-foreground', !value && 'invisible')}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => handleSelect(today)}>
            Today
          </Button>
        </div>
      </div>
    </PopoverContent>
  )

  if (allowTyping) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        {/* PopoverAnchor wraps the full input+icon div so the calendar aligns with the
            left edge of the entire field, not just the small icon button trigger. */}
        <PopoverAnchor asChild>
          <div className={cn('relative flex items-center', className)}>
            <input
              id={id}
              type="text"
              value={inputText}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="MM/DD/YYYY"
              aria-label={id ? undefined : 'Date'}
              disabled={disabled}
              className={cn(
                'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-8 text-sm shadow-sm',
                'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Open calendar"
                className="absolute right-2 flex items-center text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
        {calendarPopoverContent}
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            'h-9 w-full justify-start text-left font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          {formatted ?? placeholder}
        </Button>
      </PopoverTrigger>
      {calendarPopoverContent}
    </Popover>
  )
}
